class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');

    /** Desktop hover-open support for top nav dropdowns (mega + standard). */
    this._menuHoverCloseTimer = null;
    if (
      typeof this.mainDetailsToggle?.classList !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)').matches
    ) {
      const isMegaMenu = this.mainDetailsToggle.classList.contains('mega-menu');
      const hasStandardDesktopSubmenu = !!this.mainDetailsToggle.querySelector(
        ':scope > .header__submenu.list-menu--disclosure',
      );
      const supportsHoverOpen = isMegaMenu || hasStandardDesktopSubmenu;

      if (!supportsHoverOpen) return;

      const clearHoverCloseTimer = () => {
        if (this._menuHoverCloseTimer) {
          window.clearTimeout(this._menuHoverCloseTimer);
          this._menuHoverCloseTimer = null;
        }
      };

      const closeSiblingMenus = () => {
        document.querySelectorAll('header-menu').forEach((menu) => {
          if (menu === this) return;
          if (menu._menuHoverCloseTimer) {
            window.clearTimeout(menu._menuHoverCloseTimer);
            menu._menuHoverCloseTimer = null;
          }
          if (typeof menu.close === 'function') menu.close();
        });
      };

      const openMenu = () => {
        closeSiblingMenus();
        this.positionMegaMenu();
        this.mainDetailsToggle.setAttribute('open', '');
        const summary = this.mainDetailsToggle.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', 'true');
      };

      this.addEventListener(
        'pointerenter',
        () => {
          clearHoverCloseTimer();
          openMenu();
        },
        { passive: true },
      );

      this.addEventListener('focusin', clearHoverCloseTimer);

      this.addEventListener('pointerleave', (event) => {
        /** If moving within the mega menu subtree, ignore */
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;

        /** 140ms grace period so crossing the gap between summary and panel does not flicker */
        this._menuHoverCloseTimer = window.setTimeout(() => {
          this.close();
          this._menuHoverCloseTimer = null;
        }, 140);
      });
    }

    if (this.mainDetailsToggle?.classList.contains('mega-menu--camo')) {
      this.setupCamoPreview();
    }
  }

  positionMegaMenu() {
    if (!this.mainDetailsToggle?.classList.contains('mega-menu') || !this.header || !this.content) return;

    const summary = this.mainDetailsToggle.querySelector(':scope > summary');
    if (!summary) return;

    const headerRect = this.header.getBoundingClientRect();
    const summaryRect = summary.getBoundingClientRect();
    const panelWidth = Math.min(this.content.getBoundingClientRect().width, headerRect.width - 48);
    const desiredLeft = summaryRect.left - headerRect.left;
    const left = Math.max(24, Math.min(desiredLeft, headerRect.width - panelWidth - 24));
    this.content.style.setProperty('--cc-menu-left', `${Math.round(left)}px`);
  }

  /*
   * CAMOUFLAGES panel preview. Hovering a pattern name swaps the swatch and its
   * caption in place; the choice persists until another name is hovered, so the
   * panel never flashes back to the default while the pointer crosses a gap.
   */
  setupCamoPreview() {
    const swatch = this.mainDetailsToggle.querySelector('[data-camo-swatch]');
    const caption = this.mainDetailsToggle.querySelector('[data-camo-caption]');
    if (!swatch || !caption) return;

    const links = this.mainDetailsToggle.querySelectorAll('.mega-menu__link--pattern');
    if (!links.length) return;

    /* Warm the images so the first hover does not show an empty tile. */
    links.forEach((link) => {
      const src = link.dataset.camoImage;
      if (!src) return;
      const preload = new Image();
      preload.src = src;
    });

    const preview = (link) => {
      const src = link.dataset.camoImage;
      caption.textContent = link.dataset.camoName || link.textContent.trim();
      swatch.style.backgroundImage = src ? `url(${src})` : '';
    };

    links.forEach((link) => {
      link.addEventListener('mouseenter', () => preview(link));
      link.addEventListener('focus', () => preview(link));
    });
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (this.mainDetailsToggle.open) this.positionMegaMenu();

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
